import type { ChannelStream } from "../api";

/** A start that has not produced video by this time is a failure. */
export const LIVE_START_TIMEOUT_MS = 30_000;
/** Automatic restarts after a failed start. A manual Retry clears the count. */
export const LIVE_START_MAX_ATTEMPTS = 3;

export type LiveFailCode = "timeout" | "device_no_response" | "no_media" | "rejected";

export function liveRetryBackoffMs(attempt: number): number {
  const n = Math.max(1, attempt);
  return Math.min(2_000 * 2 ** (n - 1), 8_000);
}

/** Schedule the next 0x9101. The wait is the start timeout plus backoff, so a retry does not replace an upload that is still opening. */
export function nextLiveAttempt(input: { count: number; now: number; maxAttempts?: number }): {
  count: number;
  nextAt: number;
  exhausted: boolean;
} {
  const max = input.maxAttempts ?? LIVE_START_MAX_ATTEMPTS;
  const count = input.count + 1;
  if (count >= max) return { count, nextAt: Number.POSITIVE_INFINITY, exhausted: true };
  return {
    count,
    nextAt: input.now + LIVE_START_TIMEOUT_MS + liveRetryBackoffMs(count),
    exhausted: false,
  };
}

/**
 * Count a failure only after this start has actually ended without video.
 * Sending 0x9101 is not a failure, and the same dark start is not counted twice.
 */
export function noteLiveFailure(input: {
  count: number;
  countedStart: number | null;
  startedAt: number | null;
  failed: boolean;
  maxAttempts?: number;
}): { count: number; countedStart: number | null; exhausted: boolean; justExhausted: boolean } {
  const max = input.maxAttempts ?? LIVE_START_MAX_ATTEMPTS;
  if (!input.failed || input.startedAt == null) {
    return {
      count: input.count,
      countedStart: input.countedStart,
      exhausted: input.count >= max,
      justExhausted: false,
    };
  }
  if (input.countedStart === input.startedAt) {
    return {
      count: input.count,
      countedStart: input.countedStart,
      exhausted: input.count >= max,
      justExhausted: false,
    };
  }
  const count = input.count + 1;
  const exhausted = count >= max;
  return { count, countedStart: input.startedAt, exhausted, justExhausted: exhausted };
}

export function liveFailReason(
  channel: Pick<ChannelStream, "live" | "liveStart" | "publishing"> | null | undefined,
  startedAt: number | null,
  now: number,
  timeoutMs = LIVE_START_TIMEOUT_MS,
): { failed: boolean; code: LiveFailCode | null; message: string } {
  if (!channel || channel.live || startedAt == null) return { failed: false, code: null, message: "" };
  const phase = channel.liveStart?.phase;
  // MediaMTX path list says a publisher is up. A 401 on main_stream?session=
  // is a playback-session problem, not proof the camera never published.
  if (channel.publishing && phase !== "rejected") return { failed: false, code: null, message: "" };
  // A reject is the terminal's answer. A missing 0x0001 is not: JT1078 often
  // arrives after the 8s ACK watch, and MediaMTX is the publish check.
  if (phase === "rejected") return { failed: true, code: "rejected", message: "Device rejected the live start." };
  if (now - startedAt < timeoutMs) return { failed: false, code: null, message: "" };
  if (phase === "accepted") {
    return { failed: true, code: "no_media", message: "No Video Upload" };
  }
  if (phase === "jt1078" || channel.liveStart?.jt1078Accepted) {
    return { failed: true, code: "no_media", message: "No stream on the media server." };
  }
  if (phase === "ack_timeout") {
    return { failed: true, code: "device_no_response", message: "Device did not respond." };
  }
  if (phase === "deferred") {
    return { failed: true, code: "device_no_response", message: "Start command was not delivered." };
  }
  return { failed: true, code: "timeout", message: "Stream Failed" };
}

export function liveStreamFailed(
  channel: Pick<ChannelStream, "live" | "liveStart" | "publishing"> | null | undefined,
  startedAt: number | null,
  now: number,
  timeoutMs = LIVE_START_TIMEOUT_MS,
): boolean {
  return channelRunState({
    deviceOnline: true,
    live: channel?.live,
    publishing: channel?.publishing,
    startedAt,
    now,
    phase: channel?.liveStart?.phase,
    timeoutMs,
  }).failed;
}

export type ChannelRunState = "idle" | "starting" | "publishing" | "playing" | "recovering" | "failed" | "offline";

/** After STARTING, keep retrying in RECOVERING before FAILED. */
export const LIVE_RECOVER_WINDOW_MS = 60_000;

export function channelRunState(input: {
  deviceOnline: boolean;
  live?: boolean;
  publishing?: boolean;
  playerPlaying?: boolean;
  startedAt: number | null;
  now: number;
  phase?: string | null;
  timeoutMs?: number;
}): { state: ChannelRunState; failed: boolean; message: string } {
  if (!input.deviceOnline) return { state: "offline", failed: false, message: "" };
  if (input.playerPlaying) return { state: "playing", failed: false, message: "" };
  if (input.publishing || input.live) return { state: "publishing", failed: false, message: "" };
  if (input.startedAt == null) return { state: "idle", failed: false, message: "" };
  const timeout = input.timeoutMs ?? LIVE_START_TIMEOUT_MS;
  const age = input.now - input.startedAt;
  const reason =
    input.phase === "rejected"
      ? "Device rejected the live start."
      :     input.phase === "jt1078"
        ? "No stream on the media server."
        : input.phase === "accepted"
          ? "No Video Upload"
        : input.phase === "ack_timeout"
          ? "Device did not respond."
        : input.phase === "deferred"
          ? "Start command was not delivered."
          : "Stream Failed";
  if (input.phase === "rejected") return { state: "failed", failed: true, message: reason };
  if (age < timeout) return { state: "starting", failed: false, message: "" };
  if (age < timeout + LIVE_RECOVER_WINDOW_MS) return { state: "recovering", failed: false, message: reason };
  return { state: "failed", failed: true, message: reason };
}
